import S from 'common/service/sanctuary';
import { compose, prop } from 'ramda';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { func, bool, string, shape, oneOf, instanceOf } from 'prop-types';
import { injectIntl, FormattedMessage, intlShape, defineMessages } from 'react-intl';
import Button from 'common/mdc/button';
import RadioField from 'common/mdc/form/radio';
import { CheckboxField } from 'common/mdc/form';
import { LABEL_AFTER } from 'common/mdc/form/checkbox';
import DatetimeRange from 'common/mdc/form/datetimeRange';
import Dialog from 'common/mdc/dialog';
import withMultiSelection from 'common/mdc/table/multiSelection';
import Select, { SelectItem } from 'common/mdc/select';
import Toolbar, { ToolbarRow, ToolbarSection } from 'common/mdc/toolbar';
import Icon from 'common/mdc/icon';
import { isEmpty } from 'ramda';
import DLMSparser from '../../samples/dlmsDateTimeParser';
import DateTime from 'common/components/dateTime';
import * as R from 'ramda';
import { compareRegisters } from '@sma/store/device/meteringProfile/selectors';
import RegisterSampleValue from '@sma/components/samples/sampleValue';

import {
  actions as meteringProfileActions,
  selectors as meteringProfileSelectors,
  features,
} from '@sma/store/device/meteringProfile';

import {
  actions as onDemandReadingActions,
  selectors as onDemandReadingSelectors,
} from '@sma/store/device/onDemandReading';

import {
  actions as onDemandHistoryActions,
  selectors as onDemandHistorySelectors,
} from '@sma/store/device/onDemandHistory';

import HistorySamples from './historyTable';
import ImmediateSamples from './immediateSamples';

const messages = defineMessages({
  onDemandReading: {
    id: 'smas.devices.actions.on-demand-reading.on-demand-reading',
    defaultMessage: 'On demand reading from {deviceId}',
  },
  register: {
    id: 'smas.devices.actions.on-demand-reading.register',
    defaultMessage: 'Register',
  },
  sampleData: {
    id: 'smas.devices.actions.on-demand-reading.sampleData',
    defaultMessage: 'Sample data',
  },
  collectData: {
    id: 'smas.devices.actions.on-demand-reading.collectData',
    defaultMessage: 'Collect data',
  },
  cancel: {
    id: 'smas.devices.actions.on-demand-reading.cancel',
    defaultMessage: 'Cancel',
  },
  immediateSamples: {
    id: 'smas.devices.actions.on-demand-reading.immediateSamples',
    defaultMessage: 'Immediate samples',
  },
  sampleHistory: {
    id: 'smas.devices.actions.on-demand-reading.sampleHistory',
    defaultMessage: 'Sample history',
  },
  from: {
    id: 'smas.devices.actions.on-demand-reading.from',
    defaultMessage: 'From',
  },
  to: {
    id: 'smas.devices.actions.on-demand-reading.to',
    defaultMessage: 'To',
  },
});

const DAILY_MS = 86400000;

class OnDemandReading extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      checked: props.initialChecked,
      from: props.initialFrom,
      to: props.initialTo,
      persistReadings: false,
    };

    this.setChecked = this.setChecked.bind(this);
    this.loadSamples = this.loadSamples.bind(this);
    this.loadSamplesHistory = this.loadSamplesHistory.bind(this);
    this.handlePersistChange = this.handlePersistChange.bind(this);
  }

  componentDidMount() {
    const {
      deviceId,
      loadMeteringProfiles,
      deviceIdMaybe,
      deviceMeteringProfilesContext,
      clearHistory,
    } = this.props;

    if (deviceMeteringProfilesContext.isNothing || S.maybeToNullable(deviceIdMaybe) !== deviceId) {
      loadMeteringProfiles(deviceId);
    }

    clearHistory();
  }

  componentDidUpdate(prevProps, prevState){
    const {uiondemandpersistreadingsystemdefault} = this.props;
    if(prevProps.uiondemandpersistreadingsystemdefault !== uiondemandpersistreadingsystemdefault){
      this.setState({
        persistReadings: uiondemandpersistreadingsystemdefault === "true" ? true:false
      })
    }
  }

  setChecked(checked) {
    return this.setState({ checked });
  }

  loadSamplesHistory() {
    const { onDemandReading } = this.context;
    const { deviceId, loadSamplesHistory, meteringProfileMaybe } = this.props;
    const { from, to, persistReadings } = this.state;

    loadSamplesHistory({
      deviceId,
      from,
      to,
      onDemandReading,
      meteringProfile: S.maybeToNullable(meteringProfileMaybe),
      persistReadings
    });
  }

  loadSamples() {
    const { multiSelection, meteringProfileMaybe } = this.props;

    this.props.loadSamples({
      deviceId: this.props.deviceId,
      registers: multiSelection.getSelection()
        .map(register => ({
          ...register,
          profileObisCode: S.maybeToNullable(S.map(
            prop('profileObisCode'),
            meteringProfileMaybe,
          )),
        })),
    });
  }

  handlePersistChange() {
    this.setState({ persistReadings: !this.state.persistReadings });
  }

  render() {
    const {
      intl,
      profilesLoading,
      historyLoading,
      getProfilesMaybe,
      meteringProfileMaybe,
      samplesLoading,
      multiSelection,
      onSelectProfile,
      clearHistory,
      onClose,
      onClick,
      onDemandSamplingClose,
      deviceId,
      immediateSamplesData,
      sampleHistoryData,
      getSample,
      getRawValue,
      onDemandSamplingExport,
      onDemandSamplingExportSuccess,
      clearImmediateSamplesData,
      onDemandSamplingImmediateSamplesClose,
    } = this.props;

    const { checked, from, to, persistReadings } = this.state;

    const onSelectProfile$ = compose(
      clearImmediateSamplesData,
      clearHistory,
      multiSelection.clear,
      onSelectProfile,
    );

    const onClose$ = compose(
      checked === features.ON_DEMAND ? onDemandSamplingImmediateSamplesClose : onDemandSamplingClose,
      onClose,
    );

    const onDemandMeteringProfilesMaybe = getProfilesMaybe(checked);

    const handleExportToCSV = () => {
      // get type of data to export
      const exportOdrType = checked === features.ON_DEMAND ? "immediateSamples" : "samplesHistory";

      // fire redux action to show export progress
      onDemandSamplingExport(exportOdrType);

      // get registers
      let registers = (meteringProfileMaybe.value === undefined || meteringProfileMaybe.value.registers === undefined) ? [] : multiSelection.getItems(meteringProfileMaybe.value.registers).map(
        profile => ({
          ...profile,
          ...(checked !== features.ON_DEMAND_PROFILE && { profileObisCode: meteringProfileMaybe.value.profileObisCode })
        }),
      );

      const HYPHEN_SEPERATOR = " - ";
      const EMPTY_STRING = "";

      const getDescription = (rawValueObj) => {
        if (rawValueObj.description && rawValueObj.description !== null && rawValueObj.description !== undefined) {
          return HYPHEN_SEPERATOR + rawValueObj.description
        }
        return EMPTY_STRING;
      }

      const unitValue = (value, unit) => `${value} ${unit || ''}`;
      const getExp = value => (value.toExponential ? value.toExponential(2) : value);
      const getFixed = value => (value.toFixed ? value.toFixed(2) : value);
      const expValue = value => (value.toString().indexOf(".") == -1 ? getExp(value) : getFixed(value));

      const getNumber = (register, sampleValue, rawValueObj) => {
        const scaledValue = Number(rawValueObj.scaler ? `${rawValueObj.value}e${rawValueObj.scaler}` : rawValueObj.value);
        switch (rawValueObj.type) {
          case 'DLMS_TYPE_DATE':
            return <span className="table-value" title={sampleValue}>{sampleValue}</span>
          case 'DLMS_TYPE_TIME':
          case 'DLMS_TYPE_DATE_TIME':
            return isEmpty(sampleValue) ?
              <DLMSparser
                date={rawValueObj.value.date}
                time={rawValueObj.value.time}
                deviation={rawValueObj.value.deviation}
              /> :
              <DateTime value={sampleValue} title seconds />;
          case 'DLMS_TYPE_DOUBLE_LONG':
          case 'DLMS_TYPE_DOUBLE_U_LONG':
          case 'DLMS_TYPE_INTEGER':
          case 'DLMS_TYPE_LONG':
          case 'DLMS_TYPE_U_LONG':
          case 'DLMS_TYPE_LONG64':
          case 'DLMS_TYPE_U_LONG64':
          case 'DLMS_TYPE_FLOAT32':
          case 'DLMS_TYPE_FLOAT64':
            const cellValue = `${unitValue(
              Number.isInteger(scaledValue)
                ? scaledValue
                : expValue(scaledValue),
              rawValueObj.unit,
            )} ${getDescription(rawValueObj)}`;
            return <span className="table-value" title={cellValue}>{cellValue}</span>
          case 'DLMS_TYPE_BOOLEAN':
          case 'DLMS_TYPE_BIT_STRING':
          case 'DLMS_TYPE_OCTET_STRING':
          case 'DLMS_TYPE_VISIBLE_STRING':
            return <span className="table-value" title={sampleValue}>{sampleValue}</span>;
          default:
            const defaultCellValue = unitValue(
              Number.isInteger(scaledValue)
                ? scaledValue
                : expValue(scaledValue),
              rawValueObj.unit,
            );
            return <span className="table-value" title={defaultCellValue}>{defaultCellValue}</span>;
        }
      }

      function formatToExactUiDate(isoString) {
        const date = new Date(isoString);

        const month = date.getMonth() + 1;
        const day = date.getDate();
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');

        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;

        return `${month}/${day}/${year} ${hours}:${minutes}:${seconds} ${ampm}`;
      }

      function isIsoDateTime(value) {
        if (typeof value !== 'string') return false;

        // Matches:
        // 2026-03-31T16:00:00+05:30
        // 2026-03-31T13:45:00+0000
        // 2026-03-31T13:45:00Z
        return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}((Z)|([+-]\d{2}:?\d{2}))$/.test(value);
      }

      function extractCellValue(cell) {
        if (cell == null) return null;

        if (typeof cell === 'object' && cell.props) {
          let value = null;

          if (cell.props.value !== undefined && cell.props.value !== null) {
            value = cell.props.value;
          } else if (cell.props.children !== undefined && cell.props.children !== null) {
            value = cell.props.children;
          } else if (cell.props.title !== undefined && cell.props.title !== null) {
            value = cell.props.title;
          }

          // normalize whitespace
          if (typeof value === 'string') {
            value = value.replace(/\s+/g, ' ').trim();
          }

          //format ONLY if it is an ISO date-time string
          if (isIsoDateTime(value)) {
            return formatToExactUiDate(value);
          }

          // Apply formula ONLY for "0 A", since excel interpretes 0 A as 12:00:00 AM in excel
          if (value === '0 A') {
            value = '="0 A"';
          }

          return value;
        }

        return cell;
      }

      // Build data array for CSV
      const rows = [];
      let header = [];

      // Form header (first row in CSV)
      if (checked === features.ON_DEMAND_PROFILE) {
        let obisCodeHeaders = [];
        registers.forEach((register, i) => {
          const title = (register.name !== undefined && register.name.length > 0) ? register.name : (register.obisCode ? `${register.obisCode}/${register.attributeId}` : "");
          obisCodeHeaders.push(title);
        });
        header = [
          "Meter datetime",
          "Server time",
          ...obisCodeHeaders
        ];
      }
      else {
        header = [
          "Name",
          "ObisCode/IC",
          "Sample data"
        ];
      }

      rows.push(header);

      function getHistorySample(sample) {
        let finalRegisterSamples = [];
        const samples = (sample && sample.registerValues) || [];

        const getRegisterSample = register => { return R.find(compareRegisters(register), samples); }

        registers.forEach((register) => {
          const registerSample = getRegisterSample(register);
          let registerSampleValue = registerSample && (
            registerSample.rawValue ?
              RegisterSampleValue({ sampleValue: registerSample.rawValue, stringValue: registerSample.value })
              :
              registerSample.value
          );

          registerSampleValue = extractCellValue(registerSampleValue);
          finalRegisterSamples.push(registerSampleValue);
        });
        return finalRegisterSamples;
      }

      // data rows
      if (checked === features.ON_DEMAND_PROFILE) {

        sampleHistoryData.forEach((sample, i) => {

          const sampleTime = isIsoDateTime(sample.sampleTime) ? formatToExactUiDate(sample.sampleTime) : sample.sampleTime;
          const registeredTime = isIsoDateTime(sample.registeredTime) ? formatToExactUiDate(sample.registeredTime) : sample.registeredTime;
          const registerSamples = getHistorySample(sample);

          rows.push([
            sampleTime,
            registeredTime,
            ...registerSamples,
          ]);
        });
      }
      else {

        registers.forEach((register, i) => {
          const obisCode = register.obisCode ? `${register.obisCode}/${register.attributeId}` : "";
          let sampleValue = "-";

          sampleValue = S.maybeToNullable(getSample(register)) !== null ?
            getNumber(register, S.maybeToNullable(getSample(register)), S.maybeToNullable(getRawValue(register))) :
            S.maybeToNullable(getSample(register));

          //above code returns a react component, hence extracting value from component
          sampleValue = extractCellValue(sampleValue);

          rows.push([
            register.name,
            obisCode,
            sampleValue === null ? "-" : sampleValue,
          ]);
        });
      }

      const escapeValue = (value) => {
        if (value === null || value === undefined) return '';
        const stringValue = String(value);
        if (/[",\n]/.test(stringValue)) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      };

      const csvContent = rows
        .map(row => row.map(escapeValue).join(','))
        .join('\n');

      onDemandSamplingExportSuccess();

      //form final CSV
      const blob = new Blob(
        ['\uFEFF' + csvContent],
        { type: 'text/csv;charset=utf-8;' }
      );

      //form name of CSV file to export
      const selectedProfileName = meteringProfileMaybe !== undefined && meteringProfileMaybe.value !== undefined && meteringProfileMaybe.value.name;
      const dataTypeToExport = checked === features.ON_DEMAND_PROFILE ? `sample_history` : `immediate_samples`;
      const exportFileName = `${dataTypeToExport}_${deviceId}_${selectedProfileName}`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = exportFileName
      link.click();

      URL.revokeObjectURL(link.href);

    }

    return (
      <Dialog
        title={intl.formatMessage(messages.onDemandReading, { deviceId })}
        onClose={onClose$}
        loading={profilesLoading || samplesLoading || historyLoading}
        onClick={onClick}
        buttons={
          <Fragment>
            <Button
              onClick={onClose$}
              className="mdc-dialog__footer__button"
            >
              <FormattedMessage {...messages.cancel} />
            </Button>
            <Button
              raised
              onClick={checked === features.ON_DEMAND ? this.loadSamples : this.loadSamplesHistory}
              disabled={(!multiSelection.hasSelection() && checked === features.ON_DEMAND) || samplesLoading || historyLoading}
              className="mdc-dialog__footer__button"
            >
              <FormattedMessage {...messages.collectData} />
            </Button>
          </Fragment>
        }
      >
        <Toolbar>
          <ToolbarRow>
            <ToolbarSection className="radio-buttons" align={ToolbarSection.ALIGN_START}>
              <RadioField
                disabled={S.maybe(
                  false,
                  S.complement(meteringProfileSelectors.hasFeature(
                    features.ON_DEMAND,
                  )),
                  meteringProfileMaybe,
                )}
                id={features.ON_DEMAND}
                checked={checked === features.ON_DEMAND}
                onChange={() => this.setChecked(features.ON_DEMAND)}
                label={<FormattedMessage {...messages.immediateSamples} />}
              />
              <RadioField
                disabled={S.maybe(
                  false,
                  S.complement(meteringProfileSelectors.hasFeature(
                    features.ON_DEMAND_PROFILE,
                  )),
                  meteringProfileMaybe,
                )}
                id={features.ON_DEMAND_PROFILE}
                checked={checked === features.ON_DEMAND_PROFILE}
                onChange={() => this.setChecked(features.ON_DEMAND_PROFILE)}
                label={<FormattedMessage {...messages.sampleHistory} />}
              />
              {checked === features.ON_DEMAND_PROFILE &&
                <CheckboxField
                  label={'Persist Readings'}
                  id={`on-demand-persist-readings`}
                  labelId={`on-demand-persist-readings-label`}
                  labelPosition={LABEL_AFTER}
                  checked={persistReadings}
                  onChange={()=>this.handlePersistChange()}
                />
              }

            </ToolbarSection>
            <ToolbarSection shrink align={ToolbarSection.ALIGN_START}>
              {checked === features.ON_DEMAND_PROFILE && (
                <Fragment>
                  <DatetimeRange
                    from={from}
                    to={to}
                    onChange={period => this.setState(period)}
                  />
                </Fragment>
              )}
            </ToolbarSection>
            <ToolbarSection align={ToolbarSection.ALIGN_END}>
              {S.maybeToNullable(S.map(
                meteringProfiles => S.maybeToNullable(S.map(
                  meteringProfile => (
                    <Select
                      // this forces remount, as we change select items
                      key={checked}
                      onChange={onSelectProfile$}
                      value={meteringProfile.id}
                      id="metering-profiles-select"
                    >
                      {meteringProfiles.map(({ id, name }) => (
                        <SelectItem value={id} key={id}>
                          {name}
                        </SelectItem>
                      ))}
                    </Select>
                  ),
                  meteringProfileMaybe,
                )),
                onDemandMeteringProfilesMaybe,
              ))}
              <Fragment>
                <label htmlFor="export-csv">
                  <Icon className={`mdc-toolbar__icon
                  ${(checked === features.ON_DEMAND && (immediateSamplesData === undefined || immediateSamplesData.length === 0)) ||
                    (checked === features.ON_DEMAND_PROFILE && (sampleHistoryData === undefined || sampleHistoryData.length === 0))
                      ?
                      "disable-odr-download"
                      : ''
                    }`
                  }
                    onClick={() => { handleExportToCSV() }}
                  >archive</Icon>
                </label>
              </Fragment>
            </ToolbarSection>
          </ToolbarRow>
        </Toolbar>
        {checked === features.ON_DEMAND
          ? (
            <ImmediateSamples
              multiSelection={multiSelection}
              meteringProfileMaybe={meteringProfileMaybe}
            />
          ) : (
            <HistorySamples
              registers={S.maybeToNullable(S.map(
                meteringProfile => meteringProfile.registers,
                meteringProfileMaybe,
              ))}
            />
          )}
      </Dialog>
    );
  }
}

OnDemandReading.propTypes = ({
  intl: intlShape.isRequired,
  loadMeteringProfiles: func.isRequired,
  loadSamples: func.isRequired,
  onClose: func.isRequired,
  onSelectProfile: func.isRequired,
  profilesLoading: bool.isRequired,
  samplesLoading: bool.isRequired,
  deviceId: string.isRequired,
  onDemandSamplingClose: func.isRequired,
  historyLoading: bool.isRequired,
  getProfilesMaybe: func.isRequired,
  clearHistory: func.isRequired,
  onClick: func.isRequired,
  deviceIdMaybe: shape().isRequired,
  loadSamplesHistory: func.isRequired,
  meteringProfileMaybe: shape().isRequired,
  multiSelection: shape().isRequired,
  initialChecked: oneOf([features.ON_DEMAND, features.ON_DEMAND_PROFILE]),
  initialFrom: instanceOf(Date),
  initialTo: instanceOf(Date),
});

OnDemandReading.defaultProps = ({
  initialChecked: features.ON_DEMAND,
  initialFrom: new Date(Date.now() - DAILY_MS),
  initialTo: new Date(),
});

OnDemandReading.contextTypes = ({
  onDemandReading: func.isRequired,
});

export default compose(
  connect(
    (state, { meteringProfile }) => ({
      profilesLoading: meteringProfileSelectors.isLoading(state),
      samplesLoading: onDemandReadingSelectors.isLoading(state),
      historyLoading: onDemandHistorySelectors.isLoading(state),
      uiondemandpersistreadingsystemdefault: state.device.meteringProfile.uiondemandpersistreadingsystemdefault,
      getProfilesMaybe: onDemandReadingSelectors
        .getMeterginProfilesByFeature(state),
      deviceMeteringProfilesContext: meteringProfileSelectors.getDeviceProfilesContext(state),
      deviceIdMaybe: meteringProfileSelectors.getDeviceId(state),
      meteringProfileMaybe: S.compose(
        S.alt(onDemandReadingSelectors.getSelectedMeteringProfile(state)),
        S.alt(S.toMaybe(meteringProfile)),
      )(
        S.chain(
          S.head,
          onDemandReadingSelectors.getMeterginProfilesByFeature(state)(features.ON_DEMAND),
        ),
      ),
      immediateSamplesData: state.device.onDemandReading.data,
      sampleHistoryData: state.device.onDemandHistory.data,
      getSample: onDemandReadingSelectors.getSample(state),
      getRawValue: onDemandReadingSelectors.getRawValue(state),
      // getSelectedMeteringProfile
    }),
    ({
      loadMeteringProfiles: meteringProfileActions.load,
      loadSamples: onDemandReadingActions.load,
      loadSamplesHistory: onDemandHistoryActions.load,
      clearHistory: onDemandHistoryActions.clear,
      onSelectProfile: onDemandReadingActions.selectMeteringProfile,
      onDemandSamplingClose: onDemandReadingActions.onDemandSamplingClose,
      onDemandSamplingExport: onDemandReadingActions.onDemandSamplingExport,
      onDemandSamplingExportSuccess: onDemandReadingActions.onDemandSamplingExportSuccess,
      clearImmediateSamplesData: onDemandReadingActions.clearImmediateSamplesData,
      onDemandSamplingImmediateSamplesClose: onDemandReadingActions.onDemandSamplingImmediateSamplesClose
    }),
  ),
  injectIntl,
  withMultiSelection({
    mapItemToSelection: ({
      attributeId,
      obisCode,
    }) => ({ attributeId, obisCode }),
  }),
)(OnDemandReading);
